import { buildHubPresentation } from "./notificationContentBuilder.ts";
import { buildNotificationDeeplink } from "./deeplinkBuilders.ts";
import { notificationDeeplinkActionLabel } from "./notificationDeeplinkLabels.ts";
import type { ApiNotification } from "./notificationEntityShape.ts";
import { getRegistryEntry } from "./notificationRegistry.ts";
import type {
  HubCategory,
  HubDetailLine,
  HubVisualVariant,
} from "./notificationTypes.ts";

function str(v: unknown): string | undefined {
  if (v === null || v === undefined) return undefined;
  if (typeof v === "string") {
    const t = v.trim();
    return t.length ? t : undefined;
  }
  return undefined;
}


export type NotificationHubItem = {
  id: string;
  type: string;
  read: boolean;
  createdAt: string | null;
  title: string;
  body: string;
  details: HubDetailLine[];
  actorLabel: string;
  actorAvatar: string | null;
  category: HubCategory;
  variant: HubVisualVariant;
  deeplink: string | null;
  actionLabel: string;
  emailOnCreate: boolean;
  downloadUrl: string | null;
  downloadLabel: string | null;
  raw: ApiNotification;
};

function formatActor(user: ApiNotification["userSender"]): string {
  if (!user) return "";
  if (user.type === "COMPANY" && user.razon_social) return user.razon_social.trim();
  const name = `${user.name ?? ""} ${user.last_name ?? ""}`.trim();
  if (name) return name;
  if (user.username) return user.username;
  return "Usuario";
}

function pickDate(n: ApiNotification): string | null {
  const v =
    n.sendDate ??
    n.createdAt ??
    n.date ??
    (typeof n.timestamp === "string" ? n.timestamp : undefined);
  if (!v) return null;
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toISOString();
  } catch {
    return String(v);
  }
}

export function mapApiNotificationToHubItem(n: ApiNotification): NotificationHubItem {
  const type = String(n.type ?? "UNKNOWN");
  const entry = getRegistryEntry(type);
  const applicantName = str(n.jobPostulation?.name);
  const actorLabel =
    type === "NEW_JOB_APLICATION" && applicantName
      ? applicantName
      : formatActor(n.userSender);
  const presentation = buildHubPresentation(n, type, entry, actorLabel);
  const deeplink = buildNotificationDeeplink(n);

  let variant: HubVisualVariant = entry.variant;
  if (type === "POSTULACION_SEND") {
    const st = str(n.postulacionPermiso?.status)?.toUpperCase() ?? "";
    if (st === "NEGATED" || st === "DENIED") variant = "warning";
    else if (st === "ALLOWED") variant = "success";
  }

  const idRaw = n.id;
  const id =
    idRaw === undefined || idRaw === null
      ? ""
      : typeof idRaw === "string"
        ? idRaw
        : String(idRaw);

  return {
    id,
    type,
    read: n.view === true,
    createdAt: pickDate(n),
    title: presentation.title,
    body: presentation.body,
    details: presentation.details,
    actorLabel,
    actorAvatar: presentation.actorAvatar,
    category: entry.category,
    variant,
    deeplink,
    actionLabel: notificationDeeplinkActionLabel(deeplink),
    emailOnCreate: entry.emailOnCreate,
    downloadUrl: presentation.downloadUrl,
    downloadLabel: presentation.downloadLabel,
    raw: n,
  };
}

export function mapApiNotificationsList(list: ApiNotification[]): NotificationHubItem[] {
  return list.map(mapApiNotificationToHubItem);
}
